import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';
import classNames from 'classnames';

import messageToString from '../functions/messageToString';
import MagicLinkSignInMessagesPropType from '../definitions/MagicLinkSignInMessagesPropType';
import Sizes from '../definitions/Sizes';
import useSession from '../hooks/useSessionStore';

import Form from './Form';
import PrimaryButton from './PrimaryButton';
import Paragraph from './Paragraph';
import CondensedTheme from './CondensedTheme';
import QuestionCompact from './QuestionCompact';
import GoogleSignInButton from './GoogleSignInButton';
import AnimatedOpacityContainer from './AnimatedOpacityContainer';


function MagicLinkSignInLegacy({
    email,
    onSubmit,
    messages,
    hideDescription = false,
    shouldShowGoogleSignIn,
    onGoogleSignIn,
}) {
    const intl = useIntl();
    const sessionStore = useSession();

    const [ isGoogleSignInButtonLoaded, setIsGoogleSignInButtonLoaded ] = useState(false);

    const defaultEmail = sessionStore?.hasFetchedSession && !sessionStore?.isLoggedIn
        ? sessionStore.email
        : undefined;

    return (
        <StyledForm
            onSubmit={({ email: submittedEmail }) => onSubmit(submittedEmail)}
            shouldOnlyButtonSubmit={false}
        >
            <h1 className={classNames('title', 'rh-title-2xl', 'showDot', {
                withoutSubtitle: !messages?.MAGIC_LINK_SIGN_IN_SUBTITLE,
            })}
            >
                {messageToString(messages?.MAGIC_LINK_SIGN_IN_TITLE ?? MESSAGES.MAGIC_LINK_SIGN_IN_TITLE, intl)}
            </h1>

            <If condition={messages?.MAGIC_LINK_SIGN_IN_SUBTITLE}>
                <Paragraph
                    as="h2"
                    className="subtitle"
                    message={messages.MAGIC_LINK_SIGN_IN_SUBTITLE}
                />
            </If>

            <If condition={!hideDescription}>
                <Paragraph
                    className="description"
                    message={messages?.DESCRIPTION ?? MESSAGES.DESCRIPTION}
                />
            </If>

            <If condition={shouldShowGoogleSignIn}>
                <AnimatedOpacityContainer
                    isOpaque={isGoogleSignInButtonLoaded}
                    className="google-sign-in-section"
                >
                    <GoogleSignInButton
                        locale={intl.locale}
                        onSignIn={providerToken => onGoogleSignIn(providerToken)}
                        onLoad={() => setIsGoogleSignInButtonLoaded(true)}
                        isLegacy
                    />

                    <div
                        className="sign-in-option-divider"
                    >
                        <FormattedMessage
                            id="accounts.magic-sign-in.sign-in-option-divider"
                            defaultMessage="or"
                        />
                    </div>
                </AnimatedOpacityContainer>
            </If>

            <div className="email-input-container rh-mt-0_5">
                <div className="input-wrapper">
                    <CondensedTheme>
                        <QuestionCompact
                            field={{
                                name: 'email',
                                type: 'email',
                                value: email ?? defaultEmail,
                            }}
                            labelMessage={MESSAGES.EMAIL_LABEL}
                            shouldRemoveMargin={true}
                        />
                    </CondensedTheme>
                </div>

                <PrimaryButton
                    type="submit"
                    className="submit"
                    data-name="request-magic-link"
                    size="medium"
                    message={messages?.MAGIC_LINK_SIGN_IN_CTA ?? MESSAGES.MAGIC_LINK_SIGN_IN_CTA}
                />
            </div>
        </StyledForm>
    );
}

MagicLinkSignInLegacy.propTypes = {
    email: PropTypes.string,
    onSubmit: PropTypes.func.isRequired,
    shouldShowGoogleSignIn: PropTypes.bool,
    onGoogleSignIn: PropTypes.func,
    messages: MagicLinkSignInMessagesPropType,
    hideDescription: PropTypes.bool,
};

const StyledForm = styled(Form)`

    > .title {
        text-align: center;
    }

    > .title.withoutSubtitle {
        margin-bottom: ${Sizes.SPACING.ONE_AND_A_HALF};
    }

    > .subtitle {
        margin: 0 0 ${Sizes.SPACING.THREE} 0;
        text-align: center;
    }

    > .description {
        margin-bottom: ${Sizes.SPACING.TWO};
        text-align: center;
    }

    > .email-input-container {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        gap: ${Sizes.SPACING.ONE_AND_A_HALF};

        > .input-wrapper {
            flex-grow: 1;
            flex-basis: 60%;
            min-width: 300px;
        }

        > .submit {
            flex: 1;
            min-width: 200px;
        }
    }

    > .google-sign-in-section {
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;

        > .sign-in-option-divider {
            margin-top: ${Sizes.SPACING.HALF};
        }
    }
`;

const MESSAGES = defineMessages({
    MAGIC_LINK_SIGN_IN_TITLE: {
        id: 'Accounts.LegacyMagicLinkSignIn.Title',
        defaultMessage: 'Enter your e-mail address to join Ratehub or sign in',
    },
    MAGIC_LINK_SIGN_IN_CTA: {
        id: 'Accounts.LegacyMagicLinkSignIn.CTA',
        defaultMessage: 'Continue',
    },
    EMAIL_LABEL: {
        id: 'Accounts.LegacyMagicLinkSignIn.EmailLabel',
        defaultMessage: 'Email address',
    },
    EMAIL_PLACEHOLDER: {
        id: 'Accounts.LegacyMagicLinkSignIn.EmailPlaceholder',
        defaultMessage: 'Enter email',
    },
    DESCRIPTION: {
        id: 'Accounts.LegacyMagicLinkSignIn.Description',
        defaultMessage: 'We will send you an email with a secure sign-in link. Please click on the link to access your account.',
    },
});

export default observer(MagicLinkSignInLegacy);
